import { useState, useCallback } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function useDriverKYC() {
  const { api, AdminId } = useGlobalContext();

  const [kycDrivers, setKycDrivers] = useState([]);
  const [kycStatusLoading, setKycStatusLoading] = useState(false);
  const [kycSubmitting, setKycSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  /* ================= FETCH ALL DRIVER KYC ================= */

  const fetchDriverKycDetails = useCallback(
    async (preserveLocalUpdates = true) => {
      setLoading(true);

      try {
        const res = await api.post("/api/Driver/v1.0/getAllDriverKycDetails", {
          userid: "",
          adminId: AdminId,
        });

        if (res.data?.responseCode === "00") {
          const serverData = Array.isArray(res.data?.data)
            ? res.data.data
            : [];

          if (preserveLocalUpdates) {
            setKycDrivers((prev) =>
              serverData.map((serverDriver) => {
                const local = (prev || []).find(
                  (driver) =>
                    driver.id === serverDriver.id ||
                    driver.driver_code === serverDriver.driver_code
                );

                if (
                  local &&
                  ["Approved", "Rejected"].includes(local.status) &&
                  serverDriver.status === "Pending"
                ) {
                  return {
                    ...serverDriver,
                    status: local.status,
                  };
                }

                return serverDriver;
              })
            );
          } else {
            setKycDrivers(serverData);
          }

          return serverData;
        }

        if (shouldShowLocalError(res.data)) {
          toast.error(
            res.data?.responseMessage || "Failed to fetch Driver KYC details"
          );
        }

        setKycDrivers([]);
        return [];
      } catch (err) {
        console.error("fetchDriverKycDetails error:", err);
        toast.error("Error fetching Driver KYC details");
        setKycDrivers([]);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [api, AdminId]
  );

  /* ================= VIEW / UPDATE KYC ================= */

  const handleKycAction = useCallback(
    async (payload) => {
      if (!payload) {
        toast.error("KYC payload is required");
        return null;
      }

      setKycStatusLoading(true);

      try {
        const res = await api.post(
          "/api/Driver/v1.0/verifyDriverkycDetails",
          payload
        );

        if (res.data?.responseCode === "00") {
          toast.success(res.data?.responseMessage || "KYC updated");
          return res.data;
        }

        if (shouldShowLocalError(res.data)) {
          toast.error(res.data?.responseMessage || "KYC update failed");
        }

        return null;
      } catch (err) {
        console.error("handleKycAction error:", err);
        toast.error(err?.message || "KYC update failed");
        return null;
      } finally {
        setKycStatusLoading(false);
      }
    },
    [api]
  );

  /* ================= SUBMIT KYC STEP ================= */

  const handleStepSubmit = async (stepNumber, stepData) => {
    if (!stepNumber || !stepData) {
      toast.error("KYC step data is required");
      return false;
    }

    const userid = String(stepData?.userid || "").trim();

    if (!userid) {
      toast.error("Driver ID is required");
      return false;
    }

    try {
      setKycSubmitting(true);

      const payload = {
        userid,
        step: String(stepNumber),

        step1: {
          city: stepData?.step1?.city ?? null,
          area: stepData?.step1?.area ?? null,
        },

        step2: {
          profilePicture: stepData?.step2?.profilePicture ?? null,
        },

        step3: {
          aadharNumber: stepData?.step3?.aadharNumber ?? null,
          aadharFrontPhoto: stepData?.step3?.aadharFrontPhoto ?? null,
          aadharBackPhoto: stepData?.step3?.aadharBackPhoto ?? null,
        },

        step4: {
          panCard: stepData?.step4?.panCard ?? null,
          panCardFrontPhoto: stepData?.step4?.panCardFrontPhoto ?? null,
        },

        step5: {
          drivingLicenseNumber: stepData?.step5?.drivingLicenseNumber ?? null,
          drivingLicenseFrontPhoto:
            stepData?.step5?.drivingLicenseFrontPhoto ?? null,
        },

        step6: {
          transportTypeID: stepData?.step6?.transportTypeID ?? null,
          transportDescription: stepData?.step6?.transportDescription ?? null,
          licensePlate: stepData?.step6?.licensePlate ?? null,
          licensePlatePhoto: stepData?.step6?.licensePlatePhoto ?? null,
        },

        p1: "",
        p2: "",
        p3: "",
        p4: "",
        p5: "",
      };

      console.log(
        `FINAL KYC STEP ${stepNumber} PAYLOAD:`,
        JSON.parse(JSON.stringify(payload)),
      );

      const res = await api.post(
        "/api/Driver/v2.0/addDriverKYCDetails",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      console.log("ADD DRIVER KYC RESPONSE:", res.data);

      if (res.data?.responseCode === "00") {
        toast.success(
          res.data?.responseMessage || `Step ${stepNumber} saved successfully`,
        );

        return true;
      }

      toast.error(res.data?.responseMessage || "Failed to save KYC details");

      return false;
    } catch (err) {
      console.error("handleStepSubmit error:", err);

      toast.error(
        err?.response?.data?.responseMessage ||
          err?.message ||
          "Error saving KYC data",
      );

      return false;
    } finally {
      setKycSubmitting(false);
    }
  };

  return {
    kycDrivers,
    loading,
    fetchDriverKycDetails,

    kycStatusLoading,
    handleKycAction,

    kycSubmitting,
    handleStepSubmit,
  };
}